import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Home from './pages/Home.jsx';
import Advertisers from './pages/Advertisers.jsx';
import SpaceOwners from './pages/SpaceOwners.jsx';
import Billboard from './pages/Billboard.jsx';
import Blog from './pages/Blog.jsx';
import BlogArticle from './pages/BlogArticle.jsx';

// The original site is a set of full-page-load .html files, so an in-page
// anchor (<a href="#formats">) always landed on a freshly rendered page.
// React Router doesn't do this for free — a route change alone leaves scroll
// position wherever it was. This restores that behavior for every navigation:
// jump to the target element when the URL carries a hash, otherwise reset to
// the top of the new page.
// Retry delays (ms) for the scrollIntoView below, on top of the initial
// two-rAF attempt — a page with several images (e.g. Advertisers' hero/
// discovery card grids) keeps shifting layout as each one loads, so a single
// attempt right after the first paint can land on the target's PROVISIONAL
// position and never correct once images finish loading and the page grows
// taller. Confirmed empirically (Cypress, real Chrome): landing on
// /advertisers#booking-steps from a different page sometimes only settles
// into place 200–500ms later, not within a couple of animation frames.
const SCROLL_RETRY_DELAYS_MS = [100, 300, 600];

function ScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    const id = hash.slice(1);
    const scrollToTarget = () => document.getElementById(id)?.scrollIntoView();
    // Two rAFs: the destination page's own content (including the section
    // being linked to) must have committed to the DOM before scrollIntoView
    // has a real target to measure at all.
    requestAnimationFrame(() => requestAnimationFrame(scrollToTarget));
    const timers = SCROLL_RETRY_DELAYS_MS.map((ms) => setTimeout(scrollToTarget, ms));
    return () => timers.forEach(clearTimeout);
  }, [pathname, hash]);
  return null;
}

export default function App() {
  return (
    <>
      <ScrollToHash />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/advertisers" element={<Advertisers />} />
        <Route path="/space-owners" element={<SpaceOwners />} />
        <Route path="/billboard" element={<Billboard />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog-article" element={<BlogArticle />} />
        {/* An unmatched route rendered blank (no fallback existed) the one
            time this was actually hit: a plain <a href="#booking-steps">
            (fixed now, see Advertisers.jsx/SpaceOwners.jsx) set the WHOLE
            hash under HashRouter, which this router then tried to match as
            route /booking-steps. Kept as a backstop for the same failure mode
            from any other stray/old bare-hash link. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
